<template>
    <div
        class="row list-footer pagination-control mt-2 mb-5"
        data-cy="footer"
    >
        <div class="col-12 col-lg-4">
            <div
                class="content text-secondary text-center text-lg-left d-flex flex-row align-items-center"
                data-cy="footerDisplayingInformation"
            >
                <span data-cy="footerDisplayingNumbers">
                    Displaying {{ pageStart }} - {{ pageEnd }} of {{ totalItemCount }},
                </span>
                <div class="text-secondary page-count-select">
                    <BDropdown
                        id="countPerPage"
                        :text="`${countPerPage} per page`"
                        variant="link text-secondary"
                        class="selection"
                        :data-analytics-label="`${analyticsPrefix} Per Page Dropdown`"
                        :data-analytics-location="`${analyticsPrefix} List View`"
                        data-cy="footerCountPerPageDropdown"
                    >
                        <template #button-content>
                            {{ formatNumber(countPerPage) }} per page
                        </template>

                        <BDdItem
                            v-for="pageOption in perPageOptions"
                            :key="pageOption"
                            :active="countPerPage === pageOption"
                            :data-analytics-label="`${analyticsPrefix} Per Page Dropdown ${pageOption}`"
                            :data-analytics-location="`${analyticsPrefix} List View`"
                            :data-cy="`countPerPage${pageOption}`"
                            @click="updatePageCount(pageOption)"
                        >
                            {{ formatNumber(pageOption) }} per page
                        </BDdItem>
                    </BDropdown>
                </div>
            </div>
        </div>

        <div
            class="col-12 col-lg-4 text-center page-links"
            data-cy="footerPagination"
        >
            <BPagination
                v-model="currentPageNumber"
                align="center"
                :disabled="fetching"
                :per-page="countPerPage"
                :total-rows="totalItemCount"
            >
                <template #first-text>
                    <FontAwesomeIcon
                        icon="angle-double-left"
                        size="sm"
                        :data-analytics-label="`${analyticsPrefix} Page Number First Selected`"
                        :data-analytics-location="`${analyticsPrefix} List View`"
                        data-cy="footerPaginationFirst"
                    />
                </template>

                <template #prev-text>
                    <FontAwesomeIcon
                        icon="angle-left"
                        size="sm"
                        :data-analytics-label="`${analyticsPrefix} Page Number Previous Selected`"
                        :data-analytics-location="`${analyticsPrefix} List View`"
                        data-cy="footerPaginationPrevious"
                    />
                </template>

                <template #next-text>
                    <FontAwesomeIcon
                        icon="angle-right"
                        size="sm"
                        :data-analytics-label="`${analyticsPrefix} Page Number Next Selected`"
                        :data-analytics-location="`${analyticsPrefix} List View`"
                        data-cy="footerPaginationNext"
                    />
                </template>

                <template #last-text>
                    <FontAwesomeIcon
                        icon="angle-double-right"
                        size="sm"
                        :data-analytics-label="`${analyticsPrefix} Page Number Last Selected`"
                        :data-analytics-location="`${analyticsPrefix} List View`"
                        data-cy="footerPaginationLast"
                    />
                </template>
            </BPagination>
        </div>

        <div
            class="col-12 col-lg-4 text-right"
            data-cy="footerLoading"
        >
            <span
                v-if="fetching"
                id="listFetchingIndicator"
                data-cy="footerLoadingIndicatorContainer"
            >
                <FontAwesomeIcon
                    icon="spinner"
                    pulse
                    data-cy="footerLoadingIndicator"
                />
                loading...
            </span>
        </div>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {
        faAngleDoubleLeft,
        faAngleDoubleRight,
        faAngleLeft,
        faAngleRight,
        faSpinner,
    } from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import utils from '@imt/vue-toolbox/src/utils';
    import isEqual from 'lodash/isEqual';

    library.add(
        faAngleDoubleLeft,
        faAngleDoubleRight,
        faAngleLeft,
        faAngleRight,
        faSpinner,
    );

    export const queryParamProps = {
        // The current page number.
        page: {
            type: Number,
            default: 1,
        },
        // The query param name for the current page.
        pageQueryParam: {
            type: String,
            default: 'page',
        },
        // The number of items shown per-page.
        perPage: {
            type: Number,
            default: 25,
        },
        // The options shown in the count per-page dropdown. Defaults to 25, 50, 100, and 1000.
        perPageOptions: {
            type: Array,
            default() {
                return [25, 50, 100, 1000];
            },
        },
        // The query param name for the per-page count.
        perPageQueryParam: {
            type: String,
            default: 'perPage',
        },
    };

    /**
     * @group List View Components
     * This is the footer component that controls the per page count and page navigation.
     */
    export default {
        name: 'LFooter',
        components: {
            FontAwesomeIcon,
        },
        props: {
            // Specify if only using the footer component
            footerOnly: {
                type: Boolean,
                default: false
            },
            // Gets prefixed to all analytic attributes on elements that users interact with
            analyticsPrefix: {
                type: String,
                required: true,
            },
            // Defines if the view is fetching or not. Displays a spinner in the UI when `true`.
            fetching: {
                type: Boolean,
                required: true,
            },
            // Total number of items to be paginated
            totalItemCount: {
                type: Number,
                default: 0
            },
            ...queryParamProps,
        },
        data() {
            return {
                currentPage: 1,
                countPerPage: 25,
            };
        },
        computed: {
            currentPageNumber: {
                get() {
                    return this.currentPage;
                },
                set(newValue) {
                    if (newValue) {
                        this.jumpToPage(newValue);
                    }
                },
            },
            pageEnd() {
                return this.countPerPage > this.totalItemCount ? this.totalItemCount : parseInt(((this.currentPage - 1) * parseInt(this.countPerPage)) + parseInt(this.countPerPage));
            },
            pageStart() {
                return ((this.currentPage - 1) * this.countPerPage) + 1;
            },
        },
        created() {
            this.currentPage = this.$route.query[this.pageQueryParam] || this.page;
            this.countPerPage = this.$route.query[this.perPageQueryParam] || this.perPage;
            let newRouteQuery = {...this.$route.query, [this.pageQueryParam]: this.currentPage, [this.perPageQueryParam]: this.countPerPage};

            if (!isEqual(this.$route.query, newRouteQuery)) {
                this.$router.push({query: newRouteQuery}).catch(error => {
                    utils.console.log(error);
                });
            }

            if (!this.footerOnly) {
                this.$emit('fetch');
            }
        },
        methods: {
            formatNumber(num) {
                return new Intl.NumberFormat().format(num);
            },
            jumpToPage(linkOrPageNumber) {
                let pageNumber = linkOrPageNumber;

                if (!Number.isInteger(pageNumber)) {
                    pageNumber = pageNumber.match(/(&|\?)page=(?<page>\d+)/).groups.page;
                }

                this.currentPage = pageNumber;
                this.$router.push({query: {...this.$route.query, [this.pageQueryParam]: this.currentPage}}).catch(error => {
                    utils.console.log(error);
                });

                this.$emit('fetch');
                this.$emit('jump-to-page', pageNumber);
            },
            updatePageCount(countPerPage) {
                this.countPerPage = countPerPage;
                this.$router.push({query: {...this.$route.query, [this.pageQueryParam]: 1, [this.perPageQueryParam]: this.countPerPage}}).catch(error => {
                    utils.console.log(error);
                });

                this.$emit('fetch');
                this.$emit('update-page-count', countPerPage);
            },
        },
    };
</script>

<style
    lang="sass"
    scoped
>
    .pagination-control
        display: flex
        flex-direction: row

        .page-number,
        .paging-next,
        .paging-prev
            color: var(--kc-blue-500)

            &:hover
                cursor: pointer

        .page-number
            margin: 0 0.5rem 0 0

            &:last-of-type
                margin: 0

            &.active
                font-weight: bold
                color: var(--kc-gray-900)

                &:hover
                    cursor: default

    .page-number,
    .action
        &:hover
            color: var(--kc-blue-800)

    .page-count-select
        min-width: auto !important
        display: inline-block !important

    body.mode.dark
        .list-footer
            .page-number
                color: var(--kc-blue-400)

                &:hover
                    color: var(--kc-blue-300)

                &.active
                    color: var(--kc-gray-50)

            .action
                color: var(--kc-blue-400)

                &:hover
                    color: var(--kc-blue-300)
</style>
